import React from 'react';
import { Copy, Loader, ArrowRight, ArrowLeft, Check, X, RefreshCw, AlertTriangle, CheckCircle, Zap, Sparkles, Pencil } from 'lucide-react';

const QuestionnaireView = ({
  isDarkMode, currentQuestionnaire, formData, otherModeFields, currentRound,
  lowClarityMode, missingContextItems, additionalDetails, isViewingAnswers, isLoading,
  jiraFlowStep, jiraConsolidation, jiraChecklist, jiraGapAnswers, isFormValid,
  // Callbacks
  handleInputChange, handleCopyHint, setOtherModeFields, setAdditionalDetails,
  handleFormSubmit, handleProceedAnyway, handleStartOver, handleBackToAgentDesign, hasFormBeenEdited,
  setJiraChecklist, handleJiraChecklistConfirm, setJiraGapAnswers, handleJiraGapSubmit,
  setJiraFlowStep, setJiraConsolidation,
}) => {
  const renderFormField = (field) => {
    const { name, type, label, required, multiline, placeholder, options } = field;
    const value = formData[name] || '';

    if (name === 'mcpTools' && !formData['enableMCP']) {
      return null;
    }

    const isOutputApiDisabled = name === 'outputApiEndpoint' && formData['enableMCP'];

    switch (type) {
      case 'text':
        const isMCPToolsField = name === 'mcpTools';
        const textAreaClassName = isMCPToolsField
          ? `w-full px-3 py-2 bg-cyan-500/15 border border-cyan-400/30 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 font-mono text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`
          : `w-full px-3 py-2 bg-cyan-500/15 border border-cyan-400/30 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`;

        return (
          <div key={name} className="space-y-2">
            <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {label} {required && <span className="text-red-400">*</span>}
              {isMCPToolsField && (
                <span className="ml-2 text-xs text-blue-400">(Structured format required)</span>
              )}
            </label>
            {multiline ? (
              <textarea
                value={value}
                onChange={(e) => handleInputChange(name, e.target.value)}
                rows={isMCPToolsField ? 8 : 4}
                className={textAreaClassName}
                required={required && !isOutputApiDisabled}
                disabled={isOutputApiDisabled}
                placeholder={isMCPToolsField ?
                  "Tool Name: create_product\nWhat it does: Creates a new product in the system\nRequired info: product_name, category\nOptional info: description, price\n\nTool Name: update_product\nWhat it does: Updates an existing product\nRequired info: product_id\nOptional info: product_name, category, price" :
                  placeholder}
              />
            ) : (
              <input
                type="text"
                value={isOutputApiDisabled ? "Handled automatically by MCP" : value}
                onChange={(e) => handleInputChange(name, e.target.value)}
                className={`w-full px-3 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                  isOutputApiDisabled
                    ? 'bg-gray-600 border-gray-500 text-gray-500 cursor-not-allowed'
                    : `bg-cyan-500/15 border-cyan-400/30 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`
                }`}
                required={required && !isOutputApiDisabled}
                disabled={isOutputApiDisabled}
                placeholder={placeholder}
              />
            )}
            {isOutputApiDisabled && (
              <div className={`rounded-md p-3 mt-2 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>MCP Enabled:</p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Output delivery is handled automatically by MCP intelligent API automation. MCP will decide the best way to deliver results based on your configured tools.</p>
              </div>
            )}
            {placeholder && !isMCPToolsField && !isOutputApiDisabled && (
              <div className={`rounded-md p-3 relative ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Example:</p>
                    <p className={`text-sm select-all cursor-text ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{placeholder}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopyHint(placeholder)}
                    className={`ml-2 p-1 rounded ${isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700 dark-tooltip' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 light-tooltip'}`}
                    data-tooltip="Copy example"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
            {isMCPToolsField && (
              <div className={`rounded-md p-3 ${isDarkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-100 border border-green-300'}`}>
                <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>Enhanced MCP Tool Format:</p>
                <div className={`text-sm space-y-2 ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                  <div className={`p-2 rounded font-mono text-xs ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                    <div>Tool Name: create_product</div>
                    <div>What it does: Creates a new product</div>
                    <div>Required info: product_name, category</div>
                    <div>Optional info: description, price</div>
                  </div>
                  <p className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                    Separate each tool with a blank line. This format helps me understand what data your API needs.
                  </p>
                </div>
              </div>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div key={name} className="space-y-2">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!value}
                onChange={(e) => handleInputChange(name, e.target.checked)}
                className={`w-5 h-5 mt-0.5 text-blue-500 border-gray-300 rounded focus:ring-[#0077A8] focus:ring-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
              />
              <div className="flex-1">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  {label} {required && <span className="text-red-400">*</span>}
                </span>
                {placeholder && (
                  <div className={`mt-2 rounded-md p-3 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                    <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>What this enables:</p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{placeholder}</p>
                  </div>
                )}
              </div>
            </label>
          </div>
        );

      case 'select':
        const isOtherMode = otherModeFields[name] || false;
        return (
          <div key={name} className="space-y-2">
            <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {label} {required && <span className="text-red-400">*</span>}
            </label>
            <select
              value={isOtherMode ? '__other__' : value}
              onChange={(e) => {
                const selectedValue = e.target.value;
                if (selectedValue === '__other__') {
                  setOtherModeFields(prev => ({ ...prev, [name]: true }));
                  handleInputChange(name, ''); // Clear value so user types fresh
                } else {
                  setOtherModeFields(prev => ({ ...prev, [name]: false }));
                  handleInputChange(name, selectedValue);
                }
              }}
              className={`w-full px-3 py-2 bg-cyan-500/15 border border-cyan-400/30 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}
              required={required && !isOtherMode}
            >
              <option value="">Select an option...</option>
              {options.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
              <option value="__other__">Other (type your own)</option>
            </select>
            {isOtherMode && (
              <input
                type="text"
                value={value}
                onChange={(e) => handleInputChange(name, e.target.value)}
                className={`w-full px-3 py-2 bg-cyan-500/15 border border-cyan-400/30 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}
                placeholder="Type your answer..."
                required={required}
                autoFocus
              />
            )}
            {placeholder && (
              <div className={`rounded-md p-3 relative ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Example:</p>
                    <p className={`text-sm select-all cursor-text ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{placeholder}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopyHint(placeholder)}
                    className={`ml-2 p-1 rounded ${isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700 dark-tooltip' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 light-tooltip'}`}
                    data-tooltip="Copy example"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case 'radio':
        const isRadioOtherMode = otherModeFields[name] || false;
        return (
          <div key={name} className="space-y-2">
            <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {label} {required && <span className="text-red-400">*</span>}
            </label>
            <div className="space-y-2">
              {options.map((option, index) => (
                <label key={index} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name={name}
                    value={option}
                    checked={!isRadioOtherMode && value === option}
                    onChange={(e) => {
                      setOtherModeFields(prev => ({ ...prev, [name]: false }));
                      handleInputChange(name, e.target.value);
                    }}
                    className={`w-4 h-4 text-blue-500 border-gray-300 focus:ring-[#0077A8] focus:ring-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                    required={required && !isRadioOtherMode}
                  />
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{option}</span>
                </label>
              ))}
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name={name}
                  value="__other__"
                  checked={isRadioOtherMode}
                  onChange={() => {
                    setOtherModeFields(prev => ({ ...prev, [name]: true }));
                    handleInputChange(name, '');
                  }}
                  className={`w-4 h-4 text-blue-500 border-gray-300 focus:ring-[#0077A8] focus:ring-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                />
                <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Other</span>
              </label>
            </div>
            {isRadioOtherMode && (
              <input
                type="text"
                value={value}
                onChange={(e) => handleInputChange(name, e.target.value)}
                className={`w-full px-3 py-2 bg-cyan-500/15 border border-cyan-400/30 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}
                placeholder="Type your answer..."
                required={required}
                autoFocus
              />
            )}
            {placeholder && (
              <div className={`rounded-md p-3 relative ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Example:</p>
                    <p className={`text-sm select-all cursor-text ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{placeholder}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopyHint(placeholder)}
                    className={`ml-2 p-1 rounded ${isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700 dark-tooltip' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 light-tooltip'}`}
                    data-tooltip="Copy example"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {jiraFlowStep ? (
        /* -- Jira Review UI ------------------------------------------------- */
        <div className="space-y-6">
          {/* Step: Consolidating (skeleton loader) */}
          {jiraFlowStep === 'consolidating' && (
            <div className="space-y-6">
              <div className="space-y-2">
                <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                  Analyzing Jira Ticket
                </h3>
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                  Extracting requirements from your ticket — this takes a few seconds...
                </p>
              </div>
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className={`p-4 rounded-lg animate-pulse ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
                      <div className="flex-1 space-y-2">
                        <div className={`h-4 rounded w-3/4 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
                        <div className={`h-3 rounded w-1/2 ${isDarkMode ? 'bg-gray-600/60' : 'bg-gray-200'}`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center space-x-2 py-4">
                <Loader className={`w-5 h-5 animate-spin ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Consolidating ticket information...</span>
              </div>
            </div>
          )}

          {/* Step: Checklist -- review extracted requirements */}
          {jiraFlowStep === 'checklist' && jiraConsolidation && (
            <div className="space-y-5">
              <div className="space-y-2">
                <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                  {jiraConsolidation.title}
                </h3>
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                  Review the extracted requirements below. Uncheck anything that's not relevant for your workflow.
                </p>
              </div>

              {/* Inferred trigger & output badges */}
              {(jiraConsolidation.inferred_trigger || jiraConsolidation.inferred_output) && (
                <div className="flex flex-wrap gap-2">
                  {jiraConsolidation.inferred_trigger && (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-700/50' : 'bg-green-50 text-green-700 border border-green-200'}`}>
                      <Zap className="w-3 h-3 mr-1" /> Trigger: {jiraConsolidation.inferred_trigger}
                    </span>
                  )}
                  {jiraConsolidation.inferred_output && (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-blue-900/40 text-blue-300 border border-blue-700/50' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                      <ArrowRight className="w-3 h-3 mr-1" /> Output: {jiraConsolidation.inferred_output}
                    </span>
                  )}
                </div>
              )}

              {/* Grouped requirements by category */}
              {(() => {
                const groups = {};
                (jiraConsolidation.requirements || []).forEach(r => {
                  const cat = r.category || 'other';
                  if (!groups[cat]) groups[cat] = [];
                  groups[cat].push(r);
                });
                const categoryLabels = {
                  objective: 'Objectives',
                  input: 'Inputs',
                  processing: 'Processing',
                  output: 'Outputs',
                  integration: 'Integrations',
                  constraint: 'Constraints',
                  edge_case: 'Edge Cases',
                  other: 'Other',
                };
                const categoryColors = {
                  objective: isDarkMode ? 'text-cyan-400' : 'text-cyan-700',
                  input: isDarkMode ? 'text-green-400' : 'text-green-700',
                  processing: isDarkMode ? 'text-blue-400' : 'text-blue-700',
                  output: isDarkMode ? 'text-purple-400' : 'text-purple-700',
                  integration: isDarkMode ? 'text-orange-400' : 'text-orange-700',
                  constraint: isDarkMode ? 'text-red-400' : 'text-red-700',
                  edge_case: isDarkMode ? 'text-yellow-400' : 'text-yellow-700',
                  other: isDarkMode ? 'text-gray-400' : 'text-gray-600',
                };
                return Object.entries(groups).map(([cat, reqs]) => (
                  <div key={cat} className="space-y-2">
                    <h4 className={`text-sm font-semibold uppercase tracking-wide ${categoryColors[cat] || (isDarkMode ? 'text-gray-400' : 'text-gray-600')}`}>
                      {categoryLabels[cat] || cat}
                    </h4>
                    <div className="space-y-1">
                      {reqs.map(r => (
                        <div
                          key={r.id}
                          className={`flex items-start space-x-3 p-3 rounded-lg transition-all ${
                            jiraChecklist[r.id]
                              ? isDarkMode ? 'bg-gray-700/60 hover:bg-gray-700/80' : 'bg-white hover:bg-gray-50 shadow-sm border border-gray-200'
                              : isDarkMode ? 'bg-gray-800/40 hover:bg-gray-700/40 opacity-60' : 'bg-gray-50/50 hover:bg-gray-100/50 opacity-60'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={!!jiraChecklist[r.id]}
                            onChange={() => setJiraChecklist(prev => ({ ...prev, [r.id]: !prev[r.id] }))}
                            className="mt-1.5 w-4 h-4 rounded border-gray-300 text-[#0077A8] focus:ring-[#0077A8] cursor-pointer flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="relative group/edit">
                              <input
                                type="text"
                                value={r.text}
                                onChange={(e) => {
                                  const newText = e.target.value;
                                  setJiraConsolidation(prev => ({
                                    ...prev,
                                    requirements: prev.requirements.map(req =>
                                      req.id === r.id ? { ...req, text: newText } : req
                                    )
                                  }));
                                }}
                                className={`w-full text-sm bg-transparent border-0 border-b border-transparent focus:border-[#0077A8] focus:ring-0 pl-0 pr-6 py-0.5 transition-colors ${
                                  isDarkMode ? 'text-gray-200 placeholder-gray-500' : 'text-gray-800 placeholder-gray-400'
                                }`}
                              />
                              <Pencil className={`absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none ${
                                isDarkMode ? 'text-gray-500' : 'text-gray-400'
                              }`} />
                            </div>
                            <div className="flex items-center mt-1">
                              {r.confidence && r.confidence !== 'high' && (
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  r.confidence === 'medium'
                                    ? isDarkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-50 text-yellow-700'
                                    : isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-700'
                                }`}>
                                  {r.confidence} confidence
                                </span>
                              )}
                              {r.source && (
                                <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} ${r.confidence && r.confidence !== 'high' ? 'ml-2' : ''}`}>
                                  from {r.source}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}

              {/* Confirm button */}
              <div className={`pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  onClick={handleJiraChecklistConfirm}
                  className="w-full px-4 py-2.5 bg-[#0077A8] text-white rounded-lg hover:bg-[#006090] focus:outline-none focus:ring-2 focus:ring-[#0077A8] focus:ring-offset-2 flex items-center justify-center space-x-2 font-medium transition-colors"
                >
                  <Check className="w-4 h-4" />
                  <span>Confirm Requirements ({Object.values(jiraChecklist).filter(Boolean).length} selected)</span>
                </button>
                <button
                  onClick={() => { setJiraFlowStep(null); setJiraConsolidation(null); setJiraChecklist({}); }}
                  className={`w-full mt-2 px-4 py-2 rounded-lg flex items-center justify-center space-x-2 text-sm ${
                    isDarkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
              </div>
            </div>
          )}

          {/* Step: Gap questions */}
          {jiraFlowStep === 'gaps' && jiraConsolidation && (
            <div className="space-y-5">
              <div className="space-y-2">
                <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                  A Few More Details
                </h3>
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                  Answer these quick questions so we can design your workflow accurately.
                </p>
              </div>

              <div className="space-y-4">
                {Object.keys(jiraGapAnswers).map((question, idx) => (
                  <div key={idx} className="space-y-2">
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      {question}
                    </label>
                    <input
                      type="text"
                      value={jiraGapAnswers[question]}
                      onChange={(e) => setJiraGapAnswers(prev => ({ ...prev, [question]: e.target.value }))}
                      placeholder="Type your answer..."
                      className={`w-full px-3 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#0077A8] ${
                        isDarkMode
                          ? 'bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      }`}
                    />
                  </div>
                ))}
              </div>

              <div className={`pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  onClick={handleJiraGapSubmit}
                  disabled={Object.values(jiraGapAnswers).some(v => !v.trim())}
                  className="w-full px-4 py-2.5 bg-[#0077A8] text-white rounded-lg hover:bg-[#006090] focus:outline-none focus:ring-2 focus:ring-[#0077A8] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-medium transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>Build My Agent</span>
                </button>
                <button
                  onClick={() => setJiraFlowStep('checklist')}
                  className={`w-full mt-2 px-4 py-2 rounded-lg flex items-center justify-center space-x-2 text-sm ${
                    isDarkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to Requirements</span>
                </button>
              </div>
            </div>
          )}

          {/* Step: Building (skeleton loader) */}
          {jiraFlowStep === 'building' && (
            <div className="space-y-6">
              <div className="space-y-2">
                <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                  Designing Your Agent
                </h3>
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                  Building workflow context and generating your AI agent...
                </p>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Building context from requirements', done: true },
                  { label: 'Generating workflow architecture', done: false },
                  { label: 'Defining node schemas', done: false },
                ].map((step, idx) => (
                  <div key={idx} className={`flex items-center space-x-3 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/40' : 'bg-gray-50'}`}>
                    {step.done ? (
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    ) : idx === 1 ? (
                      <Loader className={`w-5 h-5 animate-spin flex-shrink-0 ${isDarkMode ? 'text-[#0099CC]' : 'text-[#0077A8]'}`} />
                    ) : (
                      <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`} />
                    )}
                    <span className={`text-sm ${
                      step.done
                        ? isDarkMode ? 'text-gray-300' : 'text-gray-700'
                        : idx === 1
                          ? isDarkMode ? 'text-white font-medium' : 'text-gray-900 font-medium'
                          : isDarkMode ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : currentQuestionnaire ? (
        /* -- Normal Questionnaire Form -------------------------------------- */
        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
              {currentQuestionnaire.conceptName}
            </h3>
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{currentQuestionnaire.description}</p>
          </div>

          {/* Additional Details - shown at top when clarity is below threshold */}
          {lowClarityMode === 'capped' ? (
            <div className={`p-4 rounded-lg border-2 border-solid ${isDarkMode ? 'border-blue-500 bg-blue-900/30' : 'border-blue-400 bg-blue-50'}`}>
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                <div>
                  <h4 className={`font-semibold text-base ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                    Not fully clear — your call
                  </h4>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-blue-200/80' : 'text-blue-600'}`}>
                    I'm still missing some context, but I can proceed with reasonable assumptions. What would you like to do?
                  </p>
                </div>
              </div>
              <div className="flex gap-3 mt-3">
                <button
                  onClick={handleProceedAnyway}
                  className="flex-1 px-4 py-2 bg-[#0077A8] text-white rounded-md hover:bg-[#006090] focus:outline-none focus:ring-2 focus:ring-[#0077A8] focus:ring-offset-2 flex items-center justify-center space-x-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>Proceed anyway</span>
                </button>
                <button
                  onClick={() => {
                    // Drop back to normal low-clarity mode so user can refine
                  }}
                  className={`flex-1 px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center justify-center space-x-2 ${
                    isDarkMode
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 focus:ring-gray-500'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300 focus:ring-gray-400'
                  }`}
                >
                  <Pencil className="w-4 h-4" />
                  <span>Let me refine</span>
                </button>
              </div>
            </div>
          ) : lowClarityMode && (
            <div className={`p-4 rounded-lg border-2 border-solid ${isDarkMode ? 'border-amber-500 bg-amber-900/30' : 'border-amber-400 bg-amber-50'}`}>
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                <div>
                  <h4 className={`font-semibold text-base ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                    More Details Needed
                  </h4>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-amber-200/80' : 'text-amber-600'}`}>
                    I need more context on the following to design your workflow:
                  </p>
                  <ul className={`text-sm mt-2 space-y-1 ${isDarkMode ? 'text-amber-200/70' : 'text-amber-600/80'}`}>
                    {missingContextItems.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-1">
                        <span className="text-amber-500">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <textarea
                value={additionalDetails}
                onChange={(e) => setAdditionalDetails(e.target.value)}
                placeholder="Describe the missing information in detail. For example: 'For diagnostic questions, the AI should ask about symptoms, duration, and severity. For explanation adaptation, it should adjust language based on the user's expertise level...'"
                rows={4}
                className={`w-full px-3 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                  isDarkMode
                    ? 'bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>
          )}

          <div className="space-y-6">
            {currentQuestionnaire.input?.map(renderFormField)}
          </div>

          <div className={`pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            {/* CTAs based on current round and viewing state */}
            <div className="flex flex-col space-y-3">
              {/* Primary CTA - Conditional based on viewing answers state */}
              {isViewingAnswers && !hasFormBeenEdited() ? (
                /* Back to Agent Design - when viewing answers without edits */
                <button
                  onClick={handleBackToAgentDesign}
                  disabled={isLoading}
                  className="w-full px-4 py-2 bg-[#0077A8] text-white rounded-md hover:bg-[#006090] focus:outline-none focus:ring-2 focus:ring-[#0077A8] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to Agent Design</span>
                </button>
              ) : (
                /* Generate AI Agent - normal flow or when answers have been edited */
                <button
                  onClick={handleFormSubmit}
                  disabled={!isFormValid || isLoading}
                  className="w-full px-4 py-2 bg-[#0077A8] text-white rounded-md hover:bg-[#006090] focus:outline-none focus:ring-2 focus:ring-[#0077A8] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <ArrowRight className="w-4 h-4" />
                      <span>{isViewingAnswers ? 'Regenerate AI Agent' : (currentRound === 1 ? 'Generate AI Agent' : 'Proceed to Plan')}</span>
                    </>
                  )}
                </button>
              )}

              {/* Secondary CTA - Back to Agent Design when edited, or Start Over */}
              {isViewingAnswers && hasFormBeenEdited() ? (
                <button
                  onClick={handleBackToAgentDesign}
                  disabled={isLoading}
                  className={`w-full px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 ${
                    isDarkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 focus:ring-gray-500 border border-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-400 border border-gray-300'
                  }`}
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Discard Changes</span>
                </button>
              ) : (
                <button
                  onClick={handleStartOver}
                  disabled={isLoading}
                  className={`w-full px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 ${
                    isDarkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 focus:ring-gray-500 border border-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-400 border border-gray-300'
                  }`}
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Start Over</span>
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default QuestionnaireView;
